import React, { useState, useEffect, useCallback, memo } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, MoreHorizontal, Briefcase, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FundDialog = memo(({ fund, setFund, onSave, open, onClose, isNew, saving }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent>
      <DialogHeader><DialogTitle>{isNew ? 'Add New Fund' : 'Edit Fund'}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div><Label>Name *</Label><Input value={fund.name} onChange={(e) => setFund({ ...fund, name: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Launch Year</Label><Input type="number" value={fund.launch_year} onChange={(e) => setFund({ ...fund, launch_year: parseInt(e.target.value) || 0 })} /></div>
          <div><Label>Size (USD Millions)</Label><Input type="number" value={fund.size_usd} onChange={(e) => setFund({ ...fund, size_usd: parseFloat(e.target.value) || 0 })} /></div>
        </div>
        <div><Label>Description</Label><Textarea value={fund.description} onChange={(e) => setFund({ ...fund, description: e.target.value })} /></div>
        <div><Label>Focus Areas</Label><Input value={fund.focus_areas} onChange={(e) => setFund({ ...fund, focus_areas: e.target.value })} placeholder="AI, SpaceTech, etc." /></div>
        <div><Label>Status</Label>
          <Select value={fund.status} onValueChange={(v) => setFund({ ...fund, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(isNew)} className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isNew ? 'Create' : 'Save'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
));
FundDialog.displayName = 'FundDialog';

const FundsManagement = () => {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [editFund, setEditFund] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newFund, setNewFund] = useState({ name: '', launch_year: new Date().getFullYear(), size_usd: 0, description: '', focus_areas: '', status: 'active' });
  const [saving, setSaving] = useState(false);

  const fetchFunds = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/funds?include_inactive=true`, { withCredentials: true });
      setFunds(response.data);
    } catch (error) { toast.error('Failed to fetch funds'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFunds(); }, [fetchFunds]);

  const handleDelete = async () => {
    try {
      console.log('Attempting to delete fund:', deleteId);
      const response = await axios.delete(`${API}/admin/funds/${deleteId}`, { withCredentials: true });
      console.log('Delete response:', response.data);
      toast.success('Fund deleted');
      fetchFunds();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const handleSave = useCallback(async (isNew) => {
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/funds`, newFund, { withCredentials: true });
        toast.success('Fund created');
        setShowAddDialog(false);
        setNewFund({ name: '', launch_year: new Date().getFullYear(), size_usd: 0, description: '', focus_areas: '', status: 'active' });
      } else {
        await axios.put(`${API}/admin/funds/${editFund.fund_id}`, editFund, { withCredentials: true });
        toast.success('Fund updated');
        setEditFund(null);
      }
      fetchFunds();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  }, [newFund, editFund, fetchFunds]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Funds</h1><p className="text-gray-500">Manage your investment funds</p></div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> Add Fund</Button>
      </div>

      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader><TableRow><TableHead>Fund</TableHead><TableHead>Year</TableHead><TableHead>Size</TableHead><TableHead>Focus</TableHead><TableHead>Status</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow> :
            funds.map(fund => (
              <TableRow key={fund.fund_id}>
                <TableCell><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-[#C8473A]/10 flex items-center justify-center"><Briefcase className="w-5 h-5 text-yn-brand-on-tint" /></div><div><p className="font-medium">{fund.name}</p><p className="text-sm text-gray-500">{fund.description?.slice(0, 50)}...</p></div></div></TableCell>
                <TableCell>{fund.launch_year}</TableCell>
                <TableCell>${fund.size_usd}M</TableCell>
                <TableCell className="max-w-xs truncate">{fund.focus_areas}</TableCell>
                <TableCell><Badge variant={fund.status === 'active' ? 'default' : 'secondary'}>{fund.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditFund(fund)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(fund.fund_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <FundDialog fund={newFund} setFund={setNewFund} onSave={handleSave} open={showAddDialog} onClose={() => setShowAddDialog(false)} isNew={true} saving={saving} />
      {editFund && <FundDialog fund={editFund} setFund={setEditFund} onSave={handleSave} open={!!editFund} onClose={() => setEditFund(null)} isNew={false} saving={saving} />}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Fund</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FundsManagement;
